import { useState, useRef, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Add, Global } from "iconsax-react";
import { Input, Badge, Button, ToggleButton } from "@/shared/ui";
import type { ProjectPartner } from "../types";

interface Props {
  label: string;
  value: ProjectPartner[];
  onChange: (partners: ProjectPartner[]) => void;
  addLabel: string;
  placeholder: string;
  externalLabel: string;
}

type PopoverMode = "closed" | "add" | { kind: "edit"; index: number };

export default function PartnerInput({ label, value, onChange, addLabel, placeholder, externalLabel }: Props) {
  const { t } = useTranslation("ProjectsPage");
  const toggleId = useId();
  const rawId = useId();
  const portalId = `partner-popover-${rawId.replace(/:/g, "_")}`;

  const [popoverMode, setPopoverMode] = useState<PopoverMode>("closed");
  const [draftName, setDraftName] = useState("");
  const [draftExternal, setDraftExternal] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);

  const isOpen = popoverMode !== "closed";

  useEffect(function handleClickOutside() {
    if (!isOpen) return;
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current?.contains(e.target as Node)) return;
      if (document.getElementById(portalId)?.contains(e.target as Node)) return;
      setPopoverMode("closed");
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, portalId]);

  function openAdd() {
    if (!addBtnRef.current) return;
    const rect = addBtnRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 320 - 8));
    setPopoverPos({ top: rect.top - 8, left });
    setDraftName("");
    setDraftExternal(false);
    setPopoverMode("add");
  }

  function openEdit(index: number, el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 320 - 8));
    setPopoverPos({ top: rect.top - 8, left });
    setDraftName(value[index].partner_name);
    setDraftExternal(value[index].is_external);
    setPopoverMode({ kind: "edit", index });
  }

  function closePopover() {
    setPopoverMode("closed");
  }

  function handleConfirmAdd() {
    const trimmed = draftName.trim();
    if (!trimmed) return;
    onChange([...value, { partner_name: trimmed, is_external: draftExternal }]);
    closePopover();
  }

  function handleSave() {
    if (typeof popoverMode !== "object") return;
    const trimmed = draftName.trim();
    if (!trimmed) return;
    const { index } = popoverMode;
    onChange(value.map((p, i) => (i === index ? { partner_name: trimmed, is_external: draftExternal } : p)));
    closePopover();
  }

  function handleDeleteFromPopover() {
    if (typeof popoverMode !== "object") return;
    onChange(value.filter((_, i) => i !== popoverMode.index));
    closePopover();
  }

  function handleRemove(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (!draftName.trim()) return;
      popoverMode === "add" ? handleConfirmAdd() : handleSave();
    }
    if (e.key === "Escape") {
      e.stopPropagation();
      closePopover();
    }
  }

  const popover = isOpen
    ? createPortal(
        <div
          id={portalId}
          style={{
            position: "fixed",
            top: popoverPos.top,
            left: popoverPos.left,
            transform: "translateY(-100%)",
            zIndex: 10200,
          }}
          className="flex flex-col gap-3 p-3 rounded-xl border surface-base-stroke surface-base-fill w-80">
          <Input
            placeholder={placeholder}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <div className="flex items-center justify-between gap-2">
            <label htmlFor={toggleId} className="text-label-sm content-base-primary cursor-pointer">
              {externalLabel}
            </label>
            <ToggleButton id={toggleId} checked={draftExternal} onChange={setDraftExternal} />
          </div>
          {popoverMode === "add" ? (
            <Button type="button" variant="primary" size="sm" disabled={!draftName.trim()} onClick={handleConfirmAdd}>
              {t("projectForm.actions.confirmAddPartner")}
            </Button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="content-action-negative"
                onClick={handleDeleteFromPopover}>
                {t("projectForm.actions.deletePartner")}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!draftName.trim()}
                onClick={handleSave}>
                {t("projectForm.actions.savePartner")}
              </Button>
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      <span className="text-label-sm content-base-primary">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        {value.map((partner, index) => (
          <div
            key={index}
            className="cursor-pointer"
            onClick={(e) => openEdit(index, e.currentTarget)}>
            <Badge
              variant="soft"
              color={partner.is_external ? "info" : "primary"}
              text={partner.partner_name || "—"}
              icon={partner.is_external ? <Global size={12} color="currentColor" /> : undefined}
              onRemove={() => handleRemove(index)}
              clickable
            />
          </div>
        ))}
        <button
          ref={addBtnRef}
          type="button"
          onClick={openAdd}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed surface-base-stroke text-body-regular-sm content-base-secondary hover:content-base-primary cursor-pointer transition-colors">
          <Add size={14} color="currentColor" />
          {addLabel}
        </button>
      </div>
      {popover}
    </div>
  );
}
